/**
 * Comments Service
 *
 * API endpoints for investment comments (communication log)
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { InvestmentComment, InvestmentCommentWithReplies } from '@medsuite/models';

/**
 * Create comment DTO
 */
export interface CreateCommentDto {
  comment: string;
  comment_by_email: string;
  comment_by_name: string;
  parent_comment_id?: number | null;
}

/**
 * Update comment DTO
 */
export interface UpdateCommentDto {
  comment: string;
}

/**
 * Comments list response
 */
export interface CommentsListResponse {
  investment_id: number;
  comments: InvestmentComment[];
  total: number;
}

/**
 * Threaded comments response
 */
export interface ThreadedCommentsResponse {
  investment_id: number;
  threaded: true;
  comments: InvestmentCommentWithReplies[];
  total: number;
}

/**
 * Replies response
 */
export interface RepliesResponse {
  investment_id: number;
  parent_comment_id: number;
  replies: InvestmentComment[];
  total: number;
}

/**
 * Delete response
 */
export interface DeleteCommentResponse {
  message: string;
  deleted_comment_id: number;
}

/**
 * Comments Service
 * Manages investment communication log
 */
class CommentsService {
  /**
   * Get all comments for an investment
   * Sorted by comment_date descending
   */
  async getComments(investmentId: number): Promise<ApiResponse<CommentsListResponse>> {
    return apiClient.get<CommentsListResponse>(`/investments/${investmentId}/comments`);
  }

  /**
   * Get threaded comments for an investment
   * Returns nested comment structure
   */
  async getThreadedComments(investmentId: number): Promise<ApiResponse<ThreadedCommentsResponse>> {
    return apiClient.get<ThreadedCommentsResponse>(
      `/investments/${investmentId}/comments?threaded=true`
    );
  }

  /**
   * Get paginated comments for an investment
   */
  async getCommentsPaginated(
    investmentId: number,
    params: { limit?: number; offset?: number }
  ): Promise<ApiResponse<InvestmentComment[]>> {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) {
      queryParams.set('limit', String(params.limit));
    }
    if (params.offset !== undefined) {
      queryParams.set('offset', String(params.offset));
    }

    const queryString = queryParams.toString();
    const url = `/investments/${investmentId}/comments${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<InvestmentComment[]>(url);
  }

  /**
   * Add a new comment to an investment
   */
  async addComment(
    investmentId: number,
    data: CreateCommentDto
  ): Promise<ApiResponse<InvestmentComment>> {
    return apiClient.post<InvestmentComment>(`/investments/${investmentId}/comments`, data);
  }

  /**
   * Add a reply to an existing comment
   */
  async addReply(
    investmentId: number,
    parentCommentId: number,
    data: Omit<CreateCommentDto, 'parent_comment_id'>
  ): Promise<ApiResponse<InvestmentComment>> {
    return apiClient.post<InvestmentComment>(`/investments/${investmentId}/comments`, {
      ...data,
      parent_comment_id: parentCommentId,
    });
  }

  /**
   * Update a comment's text
   */
  async updateComment(
    investmentId: number,
    commentId: number,
    data: UpdateCommentDto
  ): Promise<ApiResponse<InvestmentComment>> {
    return apiClient.put<InvestmentComment>(
      `/investments/${investmentId}/comments/${commentId}`,
      data
    );
  }

  /**
   * Delete a comment
   */
  async deleteComment(
    investmentId: number,
    commentId: number
  ): Promise<ApiResponse<DeleteCommentResponse>> {
    return apiClient.delete<DeleteCommentResponse>(
      `/investments/${investmentId}/comments/${commentId}`
    );
  }

  /**
   * Get replies to a specific comment
   */
  async getReplies(investmentId: number, commentId: number): Promise<ApiResponse<RepliesResponse>> {
    return apiClient.get<RepliesResponse>(
      `/investments/${investmentId}/comments/${commentId}/replies`
    );
  }
}

export const commentsService = new CommentsService();
